/**
 * Utilitários de arquivo do upload pela tela (M2): SHA-256 incremental (RN-GLOBAL-005) e classificação de situação
 * (novo/duplicado/planilha/sistema/inválido).
 *
 * O SHA-256 é calculado NO NAVEGADOR antes do envio — habilita o preview de duplicados e a idempotência por conteúdo.
 * Desde a ADR-043 é **incremental** (fatias de 8 MB, `hash-wasm`): um arquivo de vários GB não vai inteiro para a
 * memória. Nenhum dado pessoal é enviado a serviços externos (RN-M11-001) — o cálculo é local (WebAssembly embutido).
 */

import { createSHA256 } from 'hash-wasm';
import { ArquivoPreparado, SituacaoArquivo } from './cargas-types';
import { ArquivoSelecionado } from './pastaArrastada';
import {
  AVISO_PLANILHA_LEGADO,
  EXTENSOES_ACEITAS,
  LIMITE_BYTES_ARQUIVO,
  MOTIVO_ACIMA_DO_LIMITE,
  ehArquivoDeSistema,
  ehPlanilha,
  ehPlanilhaLegado,
  motivoForaDaCarga,
} from './formatos';

/** Tamanho da fatia lida por vez no cálculo do SHA-256 (ADR-043). */
export const FATIA_HASH_BYTES = 8 * 1024 * 1024;

/** Extrai a extensão (lowercase, sem ponto) ou string vazia. */
export function extensaoDe(nome: string): string {
  const idx = nome.lastIndexOf('.');
  if (idx <= 0 || idx === nome.length - 1) {
    return '';
  }
  return nome.slice(idx + 1).toLowerCase();
}

/**
 * true se o nome do arquivo tem extensão de um formato processado — a lista única de
 * `backend/contracts/formatos.json` (imagens e PDF, ADR-033). Desde a ADR-043 a tela aceita também as demais (o tipo
 * é decidido pelo conteúdo no M5); a extensão só serve de referência.
 */
export function ehImagem(nome: string): boolean {
  return EXTENSOES_ACEITAS.has(extensaoDe(nome));
}

/** Os bytes de uma fatia do arquivo. */
async function lerFatia(fatia: Blob): Promise<Uint8Array> {
  // `Blob.arrayBuffer()` é o caminho de produção; em ambientes onde não existe
  // (alguns runtimes de teste), `Response` fornece o mesmo buffer.
  const buffer = typeof fatia.arrayBuffer === 'function' ? await fatia.arrayBuffer() : await new Response(fatia).arrayBuffer();
  return new Uint8Array(buffer);
}

/**
 * Calcula o SHA-256 (hex) de um File **aos poucos**: lê uma fatia de `fatiaBytes` por vez e alimenta o hash
 * incremental — a memória usada é a de uma fatia, não a do arquivo (ADR-043; o Web Crypto só calcula de uma vez).
 */
export async function sha256File(file: Blob, fatiaBytes: number = FATIA_HASH_BYTES): Promise<string> {
  const hash = await createSHA256();
  hash.init();
  for (let inicio = 0; inicio < file.size; inicio += fatiaBytes) {
    hash.update(await lerFatia(file.slice(inicio, Math.min(inicio + fatiaBytes, file.size))));
  }
  return hash.digest('hex');
}

/**
 * Deriva o caminho relativo preservado de um File selecionado via
 * `webkitdirectory` (preserva subpastas — RN-M2-003). Converte `\` → `/`.
 */
export function caminhoRelativo(file: File): string {
  const rel = (file as File & { webkitRelativePath?: string }).webkitRelativePath;
  const bruto = rel && rel.length > 0 ? rel : file.name;
  return bruto.replace(/\\/g, '/');
}

/**
 * Prepara os arquivos: classifica a situação e calcula o SHA-256 do que vai. Desde a ADR-043 a tela aceita **qualquer
 * arquivo** — o tipo é decidido pelo conteúdo no M5 (compactado, PDF sem extensão, PNG com `.jpg`):
 *  - arquivo de sistema (`Thumbs.db`, `desktop.ini`, `.lnk`, … — `formatos.json`) → `sistema`: ignorado, não enviado,
 *    sem hash;
 *  - acima de 5 GB (o PUT único do S3) → `invalido`, com o motivo, sem ler o arquivo;
 *  - planilha que não seria importada (CSV, extensão de caixa mista) → `invalido`, com o motivo;
 *  - planilha de instalações (`.xlsx`, e o `.xls` com o aviso de que ainda não é lido) → `planilha`, importada sozinha
 *    (ADR-040);
 *  - o resto → `novo`; o mesmo conteúdo de novo na seleção → `duplicado`.
 * A classificação "duplicado no banco" é refinada pelo servidor no POST /cargas.
 */
export async function prepararArquivos(
  files: readonly (File | ArquivoSelecionado)[],
  onProgresso?: (feitos: number, total: number) => void,
): Promise<ArquivoPreparado[]> {
  const preparados: ArquivoPreparado[] = [];
  const vistos = new Set<string>();
  for (let i = 0; i < files.length; i += 1) {
    // Do drop de pasta o caminho já vem resolvido; do seletor, `webkitRelativePath`.
    const item = files[i];
    const file = item instanceof File ? item : item.file;
    const caminho = item instanceof File ? caminhoRelativo(item) : item.caminho;
    const base = { file, caminho, tamanho: file.size };
    const motivoFora = file.size > LIMITE_BYTES_ARQUIVO ? MOTIVO_ACIMA_DO_LIMITE : motivoForaDaCarga(file.name);
    if (ehArquivoDeSistema(caminho)) {
      preparados.push({ ...base, sha256: '', situacao: 'sistema' });
    } else if (motivoFora !== null) {
      preparados.push({ ...base, sha256: '', situacao: 'invalido', motivo: motivoFora });
    } else {
      const sha256 = await sha256File(file);
      const planilha = ehPlanilha(file.name);
      let situacao: SituacaoArquivo;
      if (vistos.has(sha256)) {
        situacao = 'duplicado';
      } else {
        situacao = planilha ? 'planilha' : 'novo';
      }
      vistos.add(sha256);
      const aviso = situacao === 'planilha' && ehPlanilhaLegado(file.name) ? { motivo: AVISO_PLANILHA_LEGADO } : {};
      preparados.push({ ...base, sha256, situacao, ...aviso });
    }
    onProgresso?.(i + 1, files.length);
  }
  return preparados;
}
